import React, { useContext, useEffect } from 'react'
import SilageStepLayout from '../SilageStepLayout'
import { Checkbox, Form, Table, Message } from 'semantic-ui-react'
import { FormFields } from '~/components/FormFields'
import { Controller, useFormContext, useWatch } from 'react-hook-form'
import { StyledGroup } from '../GrassSilageStep/style'
import { StyledDivider } from '../ElevenStep/style'
import { ClimaOptionsContext } from '.'
import { StepId } from '../../constants'
import InfoPopup from '~/components/InfoPopup'

const tableColumnHeaders = [
  {
    name: 'Arbeitsgänge',
    key: 'label',
    info: 'Bitte geben Sie hier die Anzahl der im Jahresverlauf ausgeführten Arbeitsgänge an. Haben Sie das Feld vor der Aussaat beispielsweise zweimal gegrubbert, ist in dem entsprechenden Feld eine 2 einzutragen. '
  },
  {
    name: 'Anzahl pro Jahr',
    key: 'passages',
    type: 'input',
    unit: 'Durchgänge'
  }
]

const ThirdCornStep: React.FC = () => {
  const { control, setValue, clearErrors, getValues } = useFormContext()
  const { silageTypes, pestProtectionIntensities } = useContext(ClimaOptionsContext)

  const watchPlantProtection = useWatch({ control, name: 'maizeSilage.pestProtectionIntensity' })
  const watchWorkOperations = useWatch({ control, name: 'maizeSilage.workOperations' }) || []
  const watchSeedUsed = useWatch({ control, name: 'maizeSilage.seedUsed' }) || []
  const workOperations = silageTypes.find(silageType => silageType.slug === StepId.CORN_SILAGE)?.workOperations || []

  useEffect(() => {
    const workOperationsTemp = workOperations.map(workOperation => {
      const workOpValue = watchWorkOperations.find(workOp => workOp.id === workOperation.id)
      return {
        ...workOperation,
        passages: workOpValue?.passages ?? workOperation?.defaultValue
      }
    })
    setValue('maizeSilage.workOperations', workOperationsTemp)
  }, [])

  function renderInputCellValue(name, unit: string, operationIndex: number, fieldType: string) {
    return <FormFields.Input
      placeholder={'Anzahl pro Jahr'}
      name={name}
      suffix={' mal pro Jahr'}
      onChange={(event) => {
        setValue(name, event?.target?.value?.replace(unit, ''))
        clearErrors(name)
        clearErrors('maizeSilage.workOperations')
      }}
      control={control}
      typeInput={'controled'}
      withoutAsterisk
      noErrorMessage
      allowDecimals
      decimalScale={1}
    />
  }

  function renderCell(workOperation, name, column, operationIndex: number) {
    switch (column.type) {
      case 'input':
        // eslint-disable-next-line no-case-declarations
        const fieldType = column.key
        return renderInputCellValue(name, column?.unit, operationIndex, fieldType)
      default:
        return workOperation[column.key]
    }
  }

  function renderTableBody() {
    return watchWorkOperations.map((workOperation, indexFertilizer) => {
      return <Table.Row key={workOperation.id}>
        {tableColumnHeaders.map(column => {
          const name = `maizeSilage.workOperations[${indexFertilizer}].${column.key}`
          if (column.key === 'label') {
            return <Table.Cell key={`${column.key}-${workOperation.id}`}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <label>{workOperation.label}</label>
                {workOperation.info && <InfoPopup
                  content={workOperation.info}
                  styleIcon={{ cursor: 'pointer', fontSize: '1.3em' }}
                  position={'top center'}
                />}
              </div>
            </Table.Cell>
          }
          return <Table.Cell key={`${column.key}-${column.key}`}>
            {renderCell(workOperation, name, column, indexFertilizer)}
          </Table.Cell>
        })}
      </Table.Row>
    })
  }

  function renderTable() {
    return <Table celled striped compact>
      <Table.Header>
        <Table.Row>
          {tableColumnHeaders.map(header => {
            return <Table.HeaderCell key={header.key}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <div>{header.name}</div>
                {header.info && <InfoPopup
                  content={header.info}
                  styleIcon={{ cursor: 'pointer', fontSize: '1.3em', color: 'white' }}
                  position={'top center'}
                />}
              </div>
            </Table.HeaderCell>
          })}
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {renderTableBody()}
      </Table.Body>
    </Table>
  }

  return (
    <SilageStepLayout
      infoText={`Tragen Sie die durchschnittliche Anzahl der jeweiligen Arbeitsgänge pro Jahr ein. 
        Die Abfrage bezieht sich auf eine durchschnittliche Anbaufläche von Silomais. 
        Auch von Lohnunternehmen ausgeführte Arbeitsgänge berücksichtigen.`}
    >
      <Controller
        control={control}
        name='maizeSilage.workOperations'
        rules={{
          validate: (operations) => {
            if (!operations || operations.length === 0) {
              return 'Work operations are required'
            }
            return true
          }
        }}
        render={({ fieldState: { error } }) => (
          <>
            {watchWorkOperations.length > 0 && renderTable()}
          </>
        )}
      />
      <StyledGroup widths={'equal'}>
        <Controller
          control={control}
          name='maizeSilage.pestProtectionIntensity'
          rules={{
            required: true
          }}
          render={({ fieldState: { error } }) => (
            <Form.Field error={Boolean(error)}>
              <label className='title' style={{ width: 'fit-content' }}>
                <div>{'Pflanzenschutzintensität'}</div>
                <StyledDivider />
              </label>
              <div style={{ display: 'flex', gap: 30 }}>
                {pestProtectionIntensities.map(pestProtection => {
                  return <Checkbox
                    key={pestProtection.key}
                    label={pestProtection.label}
                    checked={watchPlantProtection === pestProtection.key}
                    onClick={() => {
                      setValue('maizeSilage.pestProtectionIntensity', pestProtection.key)
                      clearErrors('maizeSilage.pestProtectionIntensity')
                    }}
                  />
                })}
              </div>
            </Form.Field>
          )}
        />
        <FormFields.Input
          control={control}
          rules={{
            validate: (value) => {
              if (!watchSeedUsed || parseInt(watchSeedUsed) === 0) {
                return true
              }
              const transformValue = typeof (value) === 'string' ? parseFloat(value.replace(',', '.')) : value
              if (transformValue > 10) {
                return 'Sie können max. 10 Einheiten pro ha eingeben. Eine Einheit entspricht 50.000 - 70.000 Körnern.'
              }
              return true
            },
            required: true
          }}
          withoutAsterisk
          noErrorMessage={(parseFloat(watchSeedUsed?.toString().replace(',', '.')) || 0) <= 1000}
          decimalScale={1}
          allowDecimals
          suffix=' Einheiten pro ha'
          typeInput={'controled'}
          label={<div className='title' style={{ width: 'fit-content' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <div>{'Saatgutmenge'}</div>
              <InfoPopup
                content={'Eine Einheit entspricht i.d.R. 50.000 - 70.000 Körnern.'}
                styleIcon={{ cursor: 'pointer', fontSize: '1.2em' }}
                position={'top center'}
              />
            </div>
            < StyledDivider />
          </div>}
          placeholder={'in Einheiten pro ha'}
          name={'maizeSilage.seedUsed'}
        />
      </StyledGroup>
    </SilageStepLayout>
  )
}

export default ThirdCornStep